
import { DiaryEntry } from "@/utils/diary";
import { memo, useMemo, useEffect } from "react";
import EntryCardsList from "./EntryCardsList";
import EmptyState from "./EmptyState";
import { useEntriesPagination } from "./hooks/useEntriesPagination";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";

interface EntriesListProps {
  entries: DiaryEntry[];
  onDelete: (id: string) => void;
  onUpdate: (updatedEntry: DiaryEntry) => void;
  searchTerm: string;
  selectedDate: Date | undefined;
  isLoading: boolean;
}

// Use memo to prevent unnecessary re-renders
const EntriesList = memo(({ 
  entries, 
  onDelete, 
  onUpdate, 
  searchTerm, 
  selectedDate,
  isLoading
}: EntriesListProps) => {
  const { language } = useLanguage();
  
  // Use the pagination hook to handle filtering and pagination
  const { filteredEntries } = useEntriesPagination(
    entries,
    searchTerm,
    selectedDate
  );
  
  // Generate structured data for search engines
  const structuredData = useMemo(() => {
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": filteredEntries.slice(0, 10).map((entry, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Article",
          "headline": entry.title || `Baby Diary Entry - ${entry.createdAt}`,
          "datePublished": entry.createdAt,
          "dateModified": entry.date || entry.createdAt, // Fixed: Using date or createdAt instead of updatedAt
          "author": {
            "@type": "Person",
            "name": "Baby Diary User"
          },
          "description": entry.entryText ? entry.entryText.substring(0, 150) + "..." : "Baby diary entry"
        }
      }))
    };
  }, [filteredEntries]);
  
  // Show loading state
  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>{t('entries.yourDiaryEntries', language)} | LittleAI Diary</title>
          <meta name="description" content={t('entries.browseAndManage', language)} />
        </Helmet>
        <EntryCardsList 
          entries={[]}
          onDelete={onDelete}
          onUpdate={onUpdate}
          isLoading={true}
        />
      </>
    );
  }
  
  // Show entries if there are any after filtering
  if (filteredEntries.length > 0) {
    return (
      <>
        <Helmet>
          <title>{t('entries.yourDiaryEntries', language)} | LittleAI Diary</title>
          <meta name="description" content={`${t('entries.browseAndManage', language)} (${filteredEntries.length} entries)`} />
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        </Helmet>
        <div className="space-y-6">
          <EntryCardsList 
            entries={filteredEntries}
            onDelete={onDelete}
            onUpdate={onUpdate}
          />
        </div>
      </>
    );
  }
  
  // Show empty state
  return (
    <>
      <Helmet>
        <title>{t('entries.noDiaryEntries', language)} | LittleAI Diary</title>
        <meta name="description" content={t('entries.createYourFirst', language)} />
      </Helmet>
      <EmptyState searchTerm={searchTerm} selectedDate={selectedDate} />
    </>
  );
});

EntriesList.displayName = "EntriesList";

export default EntriesList;
